import React, { useEffect, useMemo, useState } from "react";
import { Play, Building2, Package, Target, ChevronRight, Trophy, Award, Mic, Upload } from "lucide-react";
import { CalendarCheck } from "@phosphor-icons/react";
import type { User, AIAgent } from "../types";
import type { NavigationVersAchat } from "../lib/encadrant";
import { authApi, accountApi, challengesApi, packsApi, certificationPathsApi, recommendedSimulatorApi } from "../lib/api";
import { detectCoachKind } from "../lib/coach-kind";
import { Button } from "../components/ui/button";
import { HomeProgressWidget } from "../components/HomeProgressWidget";
import { MySeances, showsMySeances } from "../components/plan/MySeances";
import { FirstStepsCard } from "../components/FirstStepsCard";
import { QuotaExhaustedBanner } from "../components/QuotaExhaustedBanner";
import { ReviewCard } from "../components/ReviewCard";
import { ResumeCard } from "../components/ResumeCard";
import { SimulatorStudioCard, STUDIO_GRID_CLASS } from "../components/SimulatorStudioCard";
import { useTranslation } from "react-i18next";

interface DashboardPageProps {
  user: User;
  onStartTraining: (agent: AIAgent, challengeId?: string, options?: { bypassPresentation?: boolean }) => void;
  // Court-circuit agentPresentation pour Coach Prepa : on saute la page
  // intermédiaire et on va direct à /coach (qui a déjà son formulaire).
  onStartCoachDirect?: (agent: AIAgent) => void;
  onViewHistory: (sessionId?: string) => void;
  onNavigate?: (page: string) => void;
  onNavigateToChallenge?: (id: string) => void;
  onViewBrief?: (challengeId: string) => void;
  // Ouvre la modale d'enregistrement/import d'un RDV réel (carte Débriefer).
  onOpenRecord?: () => void;
}

/** Texte d'une carte de simulateur grisée faute de licence, selon le profil (décision Q6, 30/09/2026). */
export function lockedSimulatorKey(profileType: string | null | undefined): string {
  if (profileType === "ORG") return "dashboard.lockedSimulatorOrg";
  if (profileType === "RH" || profileType === "GES") return "dashboard.lockedSimulatorNeutral";
  return "dashboard.lockedSimulator";
}

export function DashboardPage({ user, onStartTraining, onStartCoachDirect, onNavigate, onNavigateToChallenge, onOpenRecord }: DashboardPageProps) {
  const { t, i18n } = useTranslation();
  const [trainingAgents, setTrainingAgents] = useState<AIAgent[]>([]);
  const [coachAgents, setCoachAgents] = useState<AIAgent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  /** Carte grisée touchée (mobile) : son explication reste affichée (décision 3.7). */
  const [lockedTapped, setLockedTapped] = useState<string | null>(null);
  /** Simulateur recommandé : en tête du studio, comme sur la page « Entraînement ». */
  const [recId, setRecId] = useState<string | null>(null);
  const [myChallenges, setMyChallenges] = useState<any[]>([]);
  const [myPaths, setMyPaths] = useState<any[]>([]);

  // « En cours » / « Tes parcours » : on masque les parcours terminés, refusés,
  // et reportés tant que la date de report n'est pas passée (l'utilisateur a
  // choisi de ne pas être sollicité). Ils restent accessibles via la page Parcours.
  const activePaths = myPaths.filter((p: any) => {
    const st = p?.enrollment?.status;
    if (st === "completed" || st === "declined") return false;
    if (st === "postponed") {
      const until = p?.enrollment?.postponedUntil ? new Date(p.enrollment.postponedUntil).getTime() : 0;
      if (until > Date.now()) return false;
    }
    return true;
  });
  const pickPathTitle = (path: any) =>
    (i18n.language === "en" ? (path?.titleEn || path?.titleFr) : path?.titleFr) || path?.titleFr || path?.title || "—";

  // Onboarding checklist ORG (aligné sur le wizard : entreprise → pack → 1er cas).
  const [onboardingSteps, setOnboardingSteps] = useState<{ company: boolean; hasPack: boolean; packConfigured: boolean; firstCase: boolean } | null>(null);

  useEffect(() => {
    authApi.getAgents()
      .then((agents: any[]) => {
        const withUnlock = agents.map((a) => ({ ...a, isUnlocked: a.isUnlocked !== undefined ? a.isUnlocked : true }));
        setTrainingAgents(withUnlock.filter((a) => a.simulatorType === "training"));
        setCoachAgents(withUnlock.filter((a) => a.simulatorType === "coach"));
      })
      .catch((err: Error) => console.error("Erreur chargement agents:", err))
      .finally(() => setLoadingAgents(false));

    certificationPathsApi.mine().then((items) => setMyPaths(items || [])).catch(() => setMyPaths([]));
    recommendedSimulatorApi.getMine().then((r) => setRecId(r?.simulator?.id || null)).catch(() => setRecId(null));

    challengesApi.listMyChallenges()
      .then((list: any[]) => setMyChallenges((list || []).filter((c: any) => {
        if (!c.enrolled) return false;
        if (c.endDate && new Date(c.endDate) < new Date()) return false;
        return true;
      })))
      .catch(() => setMyChallenges([]));
  }, []);

  useEffect(() => {
    if (user.profileType === "ORG") {
      Promise.all([
        accountApi.getCompany().catch(() => null),
        packsApi.getOneShot().catch(() => null),
        accountApi.getTargets().catch(() => []),
      ]).then(([company, packData, targets]: [any, any, any]) => {
        const oneShotPacks = packData?.packs || [];
        setOnboardingSteps({
          company: !!(company?.description && company.description.length > 10),
          hasPack: oneShotPacks.length > 0,
          packConfigured: oneShotPacks.length > 0 && !oneShotPacks.some((p: any) => p.needsConfig),
          firstCase: (targets || []).length > 0,
        });
      });
    }
  }, [user.profileType]);

  const checklistSteps = onboardingSteps
    ? [
        { key: "company", done: onboardingSteps.company, icon: Building2, label: t("onboarding.checklist.company") },
        ...(onboardingSteps.hasPack
          ? [{ key: "pack", done: onboardingSteps.packConfigured, icon: Package, label: t("onboarding.checklist.pack") }]
          : []),
        { key: "case", done: onboardingSteps.firstCase, icon: Target, label: t("onboarding.checklist.case") },
      ]
    : [];
  const checklistDoneCount = checklistSteps.filter((s) => s.done).length;
  const checklistTotal = checklistSteps.length;
  const checklistRemaining = checklistSteps.filter((s) => !s.done);

  // Actions « Sur le terrain » (RDV réels).
  const prepaAgent = coachAgents.find((a) => detectCoachKind(a.name, a.simulatorType) === "prepa");
  const debriefAgent = coachAgents.find((a) => detectCoachKind(a.name, a.simulatorType) === "debrief");
  // Coachs inclus dans toutes les formules (14/10/2026) : fermés seulement sans licence ni pack
  // actifs (`isUnlocked`, règle `userHasCoachAccess`) — bouton désactivé et raison affichée.
  const prepLocked = prepaAgent?.isUnlocked === false;
  const debriefLocked = debriefAgent?.isUnlocked === false;
  const startPrep = () => { if (prepaAgent && onStartCoachDirect) onStartCoachDirect(prepaAgent); else onNavigate?.("sessions-live"); };
  const startDebrief = () => { if (onOpenRecord) onOpenRecord(); else onNavigate?.("sales-coach-new"); };

  // Ouverture d'un parcours depuis le dashboard : le parcours d'évaluation ouvre sa
  // vue dédiée (🎯), les autres surlignent leur carte dans « Parcours certifiants ».
  const openPath = (item: any) => {
    try {
      if (item?.path?.kind === "evaluation") localStorage.setItem("bizcoach_open_eval", "1");
      else if (item?.path?.id) localStorage.setItem("bizcoach_focus_path_id", item.path.id);
    } catch { /* noop */ }
    onNavigate?.("certification-paths");
  };

  const SectionLabel = ({ children, className = "mb-3" }: { children: React.ReactNode; className?: string }) => (
    <h3 className={`${className} text-[13px] font-bold uppercase tracking-wider`} style={{ color: "#9A96AC" }}>{children}</h3>
  );

  // Studio : simulateur recommandé en tête, comme sur la page « Entraînement ».
  const studioAgents = useMemo(() => {
    const hit = recId ? trainingAgents.find((a) => a.id === recId) : undefined;
    return hit ? [hit, ...trainingAgents.filter((a) => a.id !== recId)] : trainingAgents;
  }, [trainingAgents, recId]);


  /**
   * Ce que « Passe à l'action » a encore à proposer.
   *
   * « En cours » remonte les quatre premiers challenges et parcours actifs,
   * juste au-dessus. Les répéter dans les cartes « Tes défis » et « Tes
   * parcours » affichait deux fois « Évaluation initiale » à trois blocs
   * d'écart — le lecteur cherche la différence, il n'y en a pas.
   *
   * Ces cartes reprennent donc leur rôle d'invitation : elles montrent ce qui
   * n'est PAS déjà engagé, et basculent sur « découvrir » quand tout l'est.
   */
  const shownInProgressChallenges = myChallenges.slice(0, 4);
  const shownInProgressPaths = activePaths.slice(0, 4);
  const remainingChallenges = myChallenges.filter(
    (c: any) => !shownInProgressChallenges.some((x: any) => x.id === c.id),
  );
  const remainingPaths = activePaths.filter(
    (p: any) => !shownInProgressPaths.some((x: any) => (x.path?.id ?? x.id) === (p.path?.id ?? p.id)),
  );

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Accueil — masqué sur petit écran.
          Le salut et la baseline occupaient ~130 px du premier écran d'un
          iPhone, soit un quart de ce que l'utilisateur voit en arrivant, pour
          une information qu'il connaît déjà. Sur grand écran la place ne
          manque pas : on la garde. */}
      <div className="hidden pt-1 sm:block">
        <h1 className="text-2xl md:text-3xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
          {t("dashboard.greeting", { name: user.firstName })}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.greetingSub")}</p>
      </div>

      {/* Onboarding du nouveau commercial (L8) — masqué une fois terminé */}
      <FirstStepsCard onNavigate={onNavigate} />

      {/* Reprendre où j'en étais — EN TÊTE, parce que c'est la seule chose de cet écran qui
          dise par quoi commencer : une phase de parcours entamée, un duel où c'est ton tour,
          ou la dernière session. Tout ce qui suit présente des possibilités côte à côte.
          Masquée d'elle-même quand il n'y a rien à reprendre — un compte neuf ne la voit pas. */}
      <ResumeCard onNavigate={onNavigate} />

      {/* CETTE SEMAINE — un seul récit hiérarchisé (Lot D) :
          1) la PRIORITÉ (le cap de la semaine, CTA qui lance),
          2) « Tes séances » (la file de ce qu'il y a à jouer, juste dessous),
          3) « Ma progression » à droite (radar, masqué sur mobile). */}
      <section>
        <SectionLabel>{t("dashboard.weekSection")}</SectionLabel>
        <div className="grid items-stretch gap-4 md:grid-cols-[1.5fr_1fr]">
          {/* « Tes séances » (spec/seances.md) : la file à trois sources — les quêtes
              et « Mon programme » ont été supprimés le 25/09/2026. Rendu vide sans séance. */}
          {/* Q6 (30/09/2026) : pas de « Mes sessions » pour RH / GES (ils ne s'entraînent pas). */}
          {showsMySeances(user.profileType) && <MySeances agents={trainingAgents} profileType={user.profileType} onStartTraining={onStartTraining} onNavigate={(p) => onNavigate?.(p)} onNavigateToChallenge={onNavigateToChallenge} />}
          <div data-tour="home-progress" className="hidden h-full md:block">
            <HomeProgressWidget onNavigate={onNavigate} />
          </div>
        </div>
      </section>

      {/* `onNavigate` est `App.handleNavigate`, qui accepte la cible (onglet) : le bandeau ouvre
          lui-même la destination d’achat, la même que dans Pilot (correction n°5, 30/09/2026). */}
      <QuotaExhaustedBanner userProfileType={user.profileType} onNavigate={onNavigate as NavigationVersAchat | undefined} />

      {/* « En cours » remonte AVANT « Sur le terrain » : ce qui est déjà
          engagé passe avant ce qu'on propose de commencer. Un parcours
          entamé se reprend, il ne se redécouvre pas en bas de page. */}
      {/* En cours — challenges + parcours actifs, compact (dont l'évaluation initiale).
          Ancre home-evaluation : la visite d'accueil la cible où qu'elle soit (haut/bas). */}
      {(myChallenges.length > 0 || activePaths.length > 0) && (
        <div data-tour="home-evaluation">
          <h2 className="mb-3 text-lg font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{t("dashboard.inProgress")}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {myChallenges.slice(0, 4).map((ch: any) => (
              <button key={ch.id} onClick={() => onNavigateToChallenge?.(ch.id)}
                className="flex items-center gap-3 rounded-2xl border bg-white p-4 text-left transition hover:shadow-[var(--elev-2)]"
                style={{ boxShadow: "var(--elev-1)", borderLeftWidth: 4, borderLeftColor: "#926B00" }}>
                <Trophy className="h-5 w-5 shrink-0" style={{ color: "#926B00" }} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-sm" style={{ color: "#384872" }}>{ch.name || ch.title || t("dashboard.inProgressChallenge")}</div>
                  <div className="text-xs text-muted-foreground">{ch.status === "paused" ? t("challenge.statusPaused") : t("dashboard.inProgressChallenge")}</div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0" style={{ color: "#926B00" }} />
              </button>
            ))}
            {activePaths.slice(0, 4).map((item: any) => {
              const total = item.phases?.length || 0;
              const done = (item.phases || []).filter((p: any) => p?.progress?.status === "done").length;
              const title = pickPathTitle(item.path);
              return (
                <button key={item.enrollment.id} onClick={() => openPath(item)}
                  className="flex items-center gap-3 rounded-2xl border bg-white p-4 text-left transition hover:shadow-[var(--elev-2)]"
                  style={{ boxShadow: "var(--elev-1)", borderLeftWidth: 4, borderLeftColor: "#5A4BBE" }}>
                  <Award className="h-5 w-5 shrink-0" style={{ color: "#5A4BBE" }} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-sm" style={{ color: "#384872" }}>{title}</div>
                    <div className="text-xs text-muted-foreground">{total ? t("dashboard.inProgressPhases", { done, total }) : t("dashboard.inProgressPath")}</div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0" style={{ color: "#5A4BBE" }} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* À réviser — compétences acquises puis émoussées (décroissance).
          Placée APRÈS « En cours » et avant « Sur le terrain » : ce qui est engagé passe
          d'abord, on relance ensuite sur de l'acquis qui s'effrite, et on ne propose de
          commencer qu'après. La carte se masque d'elle-même quand il n'y a rien à réviser. */}
      <ReviewCard onNavigate={onNavigate} />

      {/* Sur le terrain — RDV réels (préparer / débriefer) */}
      <section data-tour="field-cards">
        <SectionLabel>{t("dashboard.fieldSection")}</SectionLabel>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-center gap-4 rounded-2xl border bg-white p-4 shadow-[var(--elev-1)] transition hover:shadow-[var(--elev-2)]" style={{ borderTopWidth: 3, borderTopColor: "#C0423F" }}>
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: "#E7F0FA" }}>
              <CalendarCheck size={22} weight="duotone" style={{ color: "#2C6C9E" }} />
            </div>
            <div className="min-w-0 flex-1">
              <h4 data-tour="home-prep-card" className="font-semibold text-sm" style={{ color: "#384872" }}>{t("dashboard.prepCardTitle")}</h4>
              <p className="text-xs text-muted-foreground">{t("dashboard.prepCardDesc")}</p>
              {prepLocked && <p className="mt-1 text-xs" style={{ color: "#C0423F" }}>{t("apiErrors.coach_access_denied")}</p>}
            </div>
            <Button size="sm" onClick={startPrep} disabled={prepLocked} className="shrink-0 gap-1 text-white" style={{ background: "#72B76E" }}>
              <Play className="h-4 w-4" fill="white" />{t("dashboard.prepCardCta")}
            </Button>
          </div>
          {/* Débriefer = entrée directe vers la modale d'enregistrement/import (carte mise en avant) */}
          <div className="flex items-center gap-3 rounded-2xl border bg-white p-4 shadow-[var(--elev-1)] transition hover:shadow-[var(--elev-2)]" style={{ borderTopWidth: 3, borderTopColor: "#C0423F" }}>
            <button onClick={startDebrief} disabled={debriefLocked} aria-label={t("dashboard.recordCta")} className="relative disabled:opacity-40 disabled:hover:scale-100 flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-white shadow-md transition hover:scale-105" style={{ background: "#5A4BBE" }}>
              <Mic className="h-6 w-6" />
            </button>
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-sm" style={{ color: "#384872" }}>
                {t("dashboard.debriefCardTitle")}
                <span className="ml-2 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white align-middle" style={{ background: "#C0423F" }}>{t("dashboard.newBadge")}</span>
              </h4>
              <p className="text-xs text-muted-foreground">{t("dashboard.debriefCardDesc")}</p>
              {debriefLocked && <p className="mt-1 text-xs" style={{ color: "#C0423F" }}>{t("apiErrors.coach_access_denied")}</p>}
              <div className="mt-2 flex flex-wrap gap-2">
                <button onClick={startDebrief} disabled={debriefLocked} className="disabled:opacity-40 inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-bold text-white" style={{ background: "#5A4BBE" }}>
                  <span className="h-2 w-2 rounded-full bg-white" />{t("dashboard.recordCta")}
                </button>
                <button onClick={startDebrief} disabled={debriefLocked} className="disabled:opacity-40 inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border px-3.5 py-2 text-xs font-semibold" style={{ borderColor: "#988EF2", color: "#5A4BBE" }}>
                  <Upload className="h-3.5 w-3.5" />{t("dashboard.importCta")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

{/* Passe à l'action — les 3 contextes du joueur */}
      <section data-tour="dashboard-challenges">
        <SectionLabel>{t("dashboard.actionSection")}</SectionLabel>
        {/* Défis + Parcours */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {/* Tes défis */}
          <div className="flex flex-col overflow-hidden rounded-2xl border bg-white shadow-[var(--elev-1)]" style={{ borderTopWidth: 3, borderTopColor: "#926B00" }}>
            <div className="flex items-center gap-2 px-2.5 py-2 sm:px-3 sm:py-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm sm:h-8 sm:w-8" style={{ background: "#FBF3DA" }}>🏆</span>
              <div className="min-w-0">
                <div className="hidden text-[10px] font-bold uppercase tracking-wide sm:block" style={{ color: "#926B00" }}>{t("dashboard.ctxDefisWho")}</div>
                <div className="text-[11px] font-bold leading-tight sm:text-[15px]" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{t("dashboard.ctxDefisTitle")}</div>
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-1.5 p-2.5 text-[10px] text-muted-foreground sm:gap-2 sm:p-4 sm:text-xs">
              {remainingChallenges.length > 0 ? (
                <>
                  <div>{t("dashboard.ctxDefisActive", { count: remainingChallenges.length })}</div>
                  <button onClick={() => onNavigateToChallenge?.(remainingChallenges[0].id)} className="mt-auto flex min-h-[36px] items-center self-start rounded-lg px-2.5 py-1.5 text-[9px] font-bold text-white sm:px-3 sm:text-xs" style={{ background: "#926B00" }}>{t("dashboard.ctxDefisCta")} →</button>
                </>
              ) : (
                <>
                  <div>{t("dashboard.ctxDefisEmpty")}</div>
                  <button onClick={() => onNavigate?.("challenge")} className="mt-auto flex min-h-[36px] items-center self-start rounded-lg px-2.5 py-1.5 text-[9px] font-bold text-white sm:px-3 sm:text-xs" style={{ background: "#926B00" }}>{t("dashboard.ctxDefisDiscover")} →</button>
                </>
              )}
            </div>
          </div>

          {/* Tes parcours */}
          <div className="flex flex-col overflow-hidden rounded-2xl border bg-white shadow-[var(--elev-1)]" style={{ borderTopWidth: 3, borderTopColor: "#5A4BBE" }}>
            <div className="flex items-center gap-2 px-2.5 py-2 sm:px-3 sm:py-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-sm sm:h-8 sm:w-8" style={{ background: "#EDE9FB" }}>🎓</span>
              <div className="min-w-0">
                <div className="hidden text-[10px] font-bold uppercase tracking-wide sm:block" style={{ color: "#5A4BBE" }}>{t("dashboard.ctxParcoursWho")}</div>
                <div className="text-[11px] font-bold leading-tight sm:text-[15px]" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{t("dashboard.ctxParcoursTitle")}</div>
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-1.5 p-2.5 text-[10px] text-muted-foreground sm:gap-2 sm:p-4 sm:text-xs">
              {remainingPaths.length > 0 ? (() => {
                const item = remainingPaths[0];
                const total = item.phases?.length || 0;
                const done = (item.phases || []).filter((p: any) => p?.progress?.status === "done").length;
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <>
                    <div className="truncate font-semibold" style={{ color: "#384872" }}>{pickPathTitle(item.path)}</div>
                    <div>{t("dashboard.inProgressPhases", { done, total })}</div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 sm:h-2"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#5A4BBE" }} /></div>
                    <button onClick={() => openPath(item)} className="mt-auto flex min-h-[36px] items-center self-start rounded-lg px-2.5 py-1.5 text-[9px] font-bold text-white sm:px-3 sm:text-xs" style={{ background: "#5A4BBE" }}>{t("dashboard.ctxParcoursCta")} →</button>
                  </>
                );
              })() : (
                <>
                  <div>{t("dashboard.ctxParcoursEmpty")}</div>
                  <button onClick={() => onNavigate?.("certification-paths")} className="mt-auto flex min-h-[36px] items-center self-start rounded-lg px-2.5 py-1.5 text-[9px] font-bold text-white sm:px-3 sm:text-xs" style={{ background: "#5A4BBE" }}>{t("dashboard.ctxParcoursDiscover")} →</button>
                </>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* L'entraînement passe en DERNIER sur l'accueil.
          Le carrousel de simulateurs est agréable mais c'est de l'exploration,
          pas une action du jour : il s'intercalait entre « Sur le terrain » et
          les dispositifs en cours, repoussant les deux à plus de deux écrans
          de scroll sur mobile. */}
      <section data-tour="dashboard-simulators">
        {/* « Entraînement » est un TITRE DE SECTION, au même rang que « Sur le
            terrain » et « Passe à l'action » : sa place est hors du cadre. Dans
            le cadre, on nomme ce qu'on y trouve — des simulateurs. */}
        <div className="mb-3 flex items-center justify-between gap-3">
          <SectionLabel className="">{t("dashboard.trainSection")}</SectionLabel>
          <button onClick={() => onNavigate?.("m-entrainer")} className="min-h-[36px] shrink-0 whitespace-nowrap px-1 py-2 text-xs font-semibold" style={{ color: "#1E7A3C" }}>{t("dashboard.trainSeeAll")} →</button>
        </div>
        {/* Même carte et même grille que la page « Entraînement » (`SimulatorStudioCard`,
            demande du 21/10/2026) : la version propre à l'accueil donnait des cartes
            toutes de tailles différentes. */}
        {loadingAgents ? (
          <div className={STUDIO_GRID_CLASS}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[132px] animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        ) : (
          <div className={STUDIO_GRID_CLASS}>
            {studioAgents.map((agent) => {
              // Un simulateur que le compte n'a pas est ÉTEINT et non cliquable, comme sur
              // la page « Entraînement ».
              const disabled = agent.isActive === false || !agent.isUnlocked;
              // Décision 3.7 : une carte grisée dit POURQUOI, au survol (souris) comme au toucher
              // (mobile, où l'infobulle n'existe pas). La carte reste éteinte et non cliquable ;
              // un calque par-dessus reçoit le survol / le toucher et affiche l'explication.
              // Q6 (30/09/2026) : un ORG achète ou s'attribue la licence lui-même ; RH / GES n'ont
              // pas de « responsable qui ouvre une licence » — texte neutre, sans action impossible.
              const lockedWhy = agent.isActive === false ? t("dashboard.inactiveSimulator") : t(lockedSimulatorKey(user.profileType), { place: `${t("nav.myAccount")} › ${t("account.licences")}` });
              const tapped = lockedTapped === agent.id;
              return (
                <div key={agent.id} className="group relative">
                  <SimulatorStudioCard
                    agent={agent}
                    disabled={disabled}
                    isReco={agent.id === recId}
                    onStart={onStartTraining}
                    dataTour={`simulator-card-${agent.id}`}
                  />
                  {disabled && (
                    <button type="button" data-locked-why={agent.id} title={lockedWhy} aria-label={lockedWhy} aria-expanded={tapped}
                      onClick={() => setLockedTapped(tapped ? null : agent.id)}
                      className="absolute inset-0 flex cursor-help items-center justify-center rounded-2xl p-2 focus-visible:outline focus-visible:outline-2">
                      <span className={`rounded-xl bg-white/95 p-2 text-[11px] font-medium leading-snug shadow-[var(--elev-1)] ${tapped ? "block" : "hidden group-hover:block group-focus-within:block"}`} style={{ color: "#384872" }}>
                        {lockedWhy}
                      </span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      

      

      {/* Onboarding checklist ORG — masqué une fois terminé */}
      {onboardingSteps && checklistDoneCount < checklistTotal && (
        <div className="rounded-2xl border-2 border-dashed border-[#877BF2]/30 bg-gradient-to-r from-[#877BF2]/5 to-[#7CBF94]/5 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#877BF2] to-[#384872] text-2xl">🚀</div>
            <div className="flex-1">
              <h2 className="mb-1 text-lg font-bold" style={{ fontFamily: "Sora, sans-serif", color: "#384872" }}>{t("onboarding.configureSpace")}</h2>
              <p className="mb-4 text-sm text-muted-foreground">{t("onboarding.checklist.intro")}</p>
              <div className="space-y-2">
                {checklistRemaining.map((step) => (
                  <button key={step.key} className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left transition-all hover:border-[#877BF2]/50 hover:shadow-sm" onClick={() => onNavigate?.("account")}>
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400"><step.icon className="h-4 w-4" /></div>
                    <span className="flex-1 text-sm font-medium text-gray-700">{step.label}</span>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </button>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="h-2 flex-1 rounded-full bg-gray-100">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#877BF2] to-[#7CBF94] transition-all duration-500" style={{ width: `${checklistTotal > 0 ? (checklistDoneCount / checklistTotal) * 100 : 0}%` }} />
                </div>
                <span className="text-xs font-medium text-muted-foreground">{checklistDoneCount}/{checklistTotal}</span>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
